import { Box, Center, Loader } from "@mantine/core";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  AuthAppShellSkeleton,
  DiscoverBodySkeleton,
  DiscoverTopNavSkeleton,
} from "@/pages/discover/components/DiscoverPageSkeleton";
import { DISCOVER_PAGE_CONTENT_PX } from "@/pages/discover/DiscoverPage.constants";
import {
  ECOSYSTEM_HEAD_SIDEBAR_BG,
  EXPERT_SIDEBAR_BG,
  EXPO_HEAD_SIDEBAR_BG,
  FACILITATOR_SIDEBAR_BG,
} from "@/components/layout/sidebarLayout.const";

export { DiscoverBodySkeleton, AuthAppShellSkeleton } from "@/pages/discover/components/DiscoverPageSkeleton";

/**
 * Neutral boot fallback for the root Suspense boundary.
 * Must NOT look like Discover — otherwise admin/portal navigations flash the wrong chrome.
 */
export function NeutralBootFallback() {
  return (
    <Center mih="100vh" bg="#ffffff">
      <Loader size="md" color="primary" />
    </Center>
  );
}

/**
 * In-shell content fallback while a lazy page chunk loads.
 * Intentionally empty: layout chrome stays; pages own their own loaders (MRT skeletons, etc.).
 * A thin shimmer bar here stacked with page loaders and felt like an extra unexplained flash.
 */
export function AppShellRouteFallback() {
  return null;
}

type AuthShellTone = {
  sidebarBg: string;
  lightSidebar: boolean;
};

/** Match auth-gate skeleton to the portal JWT so reload never flashes the wrong sidebar color. */
function resolveAuthShellTone(explicitBg?: string): AuthShellTone {
  if (explicitBg) {
    return { sidebarBg: explicitBg, lightSidebar: explicitBg === "#ffffff" || explicitBg === "#fff" };
  }

  const token = Cookies.get("token");
  if (!token) {
    return { sidebarBg: "var(--mantine-color-primary-6)", lightSidebar: false };
  }

  try {
    const role = jwtDecode<{ role?: string }>(token).role;
    switch (role) {
      case "ECOSYSTEM_HEAD":
      case "ECOSYSTEM_SUBHEAD":
        return { sidebarBg: ECOSYSTEM_HEAD_SIDEBAR_BG, lightSidebar: false };
      case "EXPERT":
        return { sidebarBg: EXPERT_SIDEBAR_BG, lightSidebar: false };
      case "FACILITATOR":
        return { sidebarBg: FACILITATOR_SIDEBAR_BG, lightSidebar: false };
      case "EXPO_HEAD":
        return { sidebarBg: EXPO_HEAD_SIDEBAR_BG, lightSidebar: false };
      case "ADMIN":
      case "SUB_ADMIN":
        return { sidebarBg: "#ffffff", lightSidebar: true };
      default:
        // CLIENT / MANAGER / EXPO_VISITOR → company primary blue
        return { sidebarBg: "var(--mantine-color-primary-6)", lightSidebar: false };
    }
  } catch {
    return { sidebarBg: "var(--mantine-color-primary-6)", lightSidebar: false };
  }
}

/**
 * Root Suspense boot: keep portal auth shell when a JWT exists so lazy layout
 * chunks do not wipe AuthRouteFallback into a white spinner (2nd shimmer).
 * Guests / no-token keep a neutral white loader.
 */
export function BootFallback() {
  if (typeof document !== "undefined" && Cookies.get("token")) {
    return <AuthRouteFallback />;
  }
  return <NeutralBootFallback />;
}

/**
 * Auth session / user hydration gate — sidebar color follows JWT role
 * (ecosystem-head maroon, client primary blue, etc.).
 */
export function AuthRouteFallback({ sidebarBg }: { sidebarBg?: string } = {}) {
  const tone = resolveAuthShellTone(sidebarBg);
  return <AuthAppShellSkeleton sidebarBg={tone.sidebarBg} lightSidebar={tone.lightSidebar} />;
}

/**
 * Guest Suspense fallback: landing navbar + Discover body (guest explore only).
 */
export function RouteFallback() {
  return (
    <Box style={{ minHeight: "100vh", background: "#ffffff" }}>
      <DiscoverTopNavSkeleton />
      <DiscoverBodySkeleton />
    </Box>
  );
}

/** Soft content placeholder under LandingLayout (nav already mounted). */
export function LandingContentFallback() {
  return (
    <Box px={DISCOVER_PAGE_CONTENT_PX} py="md">
      <Box className="gt-shell-shimmer-bar" maw={240} />
    </Box>
  );
}
